import type { AxiosRequestConfig, AxiosResponse } from 'axios';

import axiosInstance from '@api/axios.ts';
import { urls } from '@api/urls.ts';

/**
 * Demand letter test playground, staff only (ZLAYT-1660). Every call answers
 * 404 unless the server's playground setting is on and the user is a
 * superuser. A run writes letters on a made-up test case from a copy of the
 * demand letter setup; the live setup is never changed.
 */

/**
 * The prompts of one setup. Each section's rows are lists keyed by type
 * (generation, extraction): row 1 is the main prompt, later rows refine it.
 */
export interface PlaygroundPrompts {
  name?: string;
  version_prompts: Record<string, string>;
  sections: Record<string, Record<string, string[]>>;
}

export interface PlaygroundCase {
  id: number;
  label: string;
}

export interface PlaygroundModel {
  id: string;
  label: string;
  current: boolean;
}

export interface PlaygroundOptions {
  enabled: boolean;
  cases: PlaygroundCase[];
  models: PlaygroundModel[];
  sections: string[];
  current_prompts: PlaygroundPrompts;
  style_example_available: boolean;
}

export type PlaygroundRunStatus = 'running' | 'scoring' | 'done' | 'failed';

export interface PlaygroundCheck {
  id: string;
  label: string;
  passed: boolean;
  detail: string[];
}

export interface PlaygroundWrongFact {
  quote: string;
  record_says: string;
}

export interface PlaygroundUnsupported {
  quote: string;
  kind: string;
  why: string;
}

/**
 * A letter. Only a done letter carries its text and scores; a running one
 * may be only {status, phase} ("scoring" while it is being graded).
 */
export interface PlaygroundLetter {
  status: 'running' | 'done' | 'failed';
  phase?: 'scoring' | string;
  error?: string;
  /** In letter order; text is plain text or simple HTML. */
  sections?: { name: string; text: string }[];
  score?: number | null;
  meets_bar?: boolean | null;
  /** False when the grader did not answer: the score is the fixed checks only. */
  judged?: boolean;
  judge_scores?: Record<string, number> | null;
  checks?: PlaygroundCheck[];
  wrong_facts?: PlaygroundWrongFact[];
  unsupported?: PlaygroundUnsupported[];
  summary?: string;
  cost_usd?: number | null;
  seconds?: number | null;
  words?: number | null;
  /**
   * Set when this letter was reused from an earlier run (same case, model
   * and current prompts); its cost and time belong to that run.
   */
  reused_from?: number;
}

export interface PlaygroundRun {
  id: number;
  name: string;
  status: PlaygroundRunStatus;
  error: string;
  created_at: string;
  case: PlaygroundCase;
  writer_model: string;
  use_style_example: boolean;
  prompts: PlaygroundPrompts;
  letters: {
    new: PlaygroundLetter | null;
    current: PlaygroundLetter | null;
  };
}

export interface PlaygroundRunSummary {
  id: number;
  name: string;
  status: PlaygroundRunStatus;
  created_at: string;
  case_label: string;
  writer_model: string;
  use_style_example: boolean;
  new_score: number | null;
  current_score: number | null;
}

export interface StartPlaygroundRun {
  case_id: number;
  name: string;
  writer_model: string;
  use_style_example: boolean;
  prompts: PlaygroundPrompts;
}

/**
 * The answers the page acts on itself. The shared axios instance rejects
 * with the response body alone (no status), so these come back as responses
 * and are rejected here with their status kept. A 401 still goes through the
 * instance (sign-in renewal) and a 5xx is a plain failure.
 */
const HANDLED = [400, 403, 404, 429];

export class PlaygroundRequestError extends Error {
  response: { status: number; data: unknown };

  constructor(status: number, data: unknown) {
    super(`Request failed with status ${status}`);
    this.name = 'PlaygroundRequestError';
    this.response = { status, data };
  }
}

const config: AxiosRequestConfig = {
  validateStatus: (status) =>
    (status >= 200 && status < 300) || HANDLED.includes(status),
};

const dataOf = <T>(response: AxiosResponse<T>): T => {
  if (HANDLED.includes(response.status)) {
    throw new PlaygroundRequestError(response.status, response.data);
  }
  return response.data;
};

export const getPlaygroundOptions = async (): Promise<PlaygroundOptions> =>
  dataOf(await axiosInstance.get(urls.demandPlayground.options, config));

export const listPlaygroundRuns = async (): Promise<PlaygroundRunSummary[]> =>
  dataOf<{ results?: PlaygroundRunSummary[] }>(
    await axiosInstance.get(urls.demandPlayground.runs, config)
  )?.results ?? [];

export const getPlaygroundRun = async (runId: number): Promise<PlaygroundRun> =>
  dataOf(await axiosInstance.get(urls.demandPlayground.run(runId), config));

export const startPlaygroundRun = async (
  body: StartPlaygroundRun
): Promise<PlaygroundRun> =>
  dataOf(await axiosInstance.post(urls.demandPlayground.runs, body, config));

export const rerunPlaygroundRun = async (
  runId: number
): Promise<PlaygroundRun> =>
  dataOf(
    await axiosInstance.post(urls.demandPlayground.rerun(runId), {}, config)
  );
